/**
 * Parsing for the inline pause tag used in AI voiceover reword text. A pause is written as
 * `<pause seconds="N"/>` (N may be fractional, e.g. 2.3) and reads back the same way in the
 * transcript the agent sees, so the read and write representations are symmetric.
 *
 * The tag is deliberately XML so it can never be mistaken for a spoken word token.
 */

// Matches <pause seconds="2.3"/> with single or double quotes and flexible whitespace. The
// self-closing slash is optional: models often echo the tag back as <pause seconds="2.3">, and a
// strict parse turns it into two unmatchable words, failing every placement that spans a pause.
export const PAUSE_TAG_REGEX = /<pause\s+seconds=["']([0-9]*\.?[0-9]+)["']\s*\/?>/g;

export type ParsedPause = {
    // Index into the parsed word list that this pause follows. 0 means a leading pause (before
    // the first word); a value equal to words.length means a trailing pause.
    afterWordIndex: number;
    ms: number;
};

export type ParsedReplacement = {
    /** The spoken words, with all pause tags removed. */
    words: string[];
    /** Pauses in document order, positioned relative to the word list. */
    pauses: ParsedPause[];
};

/** Split text into whitespace-separated words, dropping empties. */
export const wordsOf = (text: string): string[] => text.split(/\s+/).filter((w) => w !== '');

const toWords = wordsOf;

/**
 * Split a reword replacement into its spoken words and the pauses interleaved between them.
 * Fractional seconds are supported and converted to milliseconds.
 */
export function parseReplacementWithPauses(replacement: string): ParsedReplacement {
    const words: string[] = [];
    const pauses: ParsedPause[] = [];

    let lastIndex = 0;
    // Reset the shared regex's cursor; it is declared with the global flag.
    PAUSE_TAG_REGEX.lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = PAUSE_TAG_REGEX.exec(replacement)) !== null) {
        words.push(...toWords(replacement.slice(lastIndex, match.index)));
        pauses.push({ afterWordIndex: words.length, ms: Math.round(parseFloat(match[1]) * 1000) });
        lastIndex = match.index + match[0].length;
    }
    words.push(...toWords(replacement.slice(lastIndex)));

    return { words, pauses };
}

/** True when two word sequences are identical (used to decide whether a text regen is needed). */
export function sameWords(a: string[], b: string[]): boolean {
    return a.length === b.length && a.every((word, i) => word === b[i]);
}
